import { FC, useEffect, useState, useMemo, ReactNode } from 'react';
import { useLocation, Link } from 'react-router-dom';
import styles from './breadcrumb.module.css';

const SafeLink = Link as FC<{
  to: string;
  className?: string;
  children: ReactNode;
}>;

export interface BreadcrumbItem {
  name: string | undefined;
  path?: string;
}

export interface BreadcrumbProps {
  className?: string;
  items?: BreadcrumbItem[];
  useCurrentRoute?: boolean;
  titleBread?: string;
}

export const Breadcrumb: FC<BreadcrumbProps> = ({
  className = '',
  items,
  useCurrentRoute = true,
  titleBread
}) => {
  const { pathname } = useLocation();
  const [breadcrumbItems, setBreadcrumbItems] = useState<BreadcrumbItem[]>([]);
  const nameFromSegment = (segment: string) =>
    segment.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

  const generateBreadcrumb = useMemo(() => {
    const segments = pathname.split(/[?#]/)[0].split('/').filter(Boolean);
    const result: BreadcrumbItem[] = [{ name: 'Início', path: '/' }];
    segments.forEach((_, i) => {
      const path = `/${segments.slice(0, i + 1).join('/')}`;
      
      result.push({
        name: (i === segments.length - 1) ? titleBread :  nameFromSegment(segments[i]),
        path: i < segments.length - 1 ? path : undefined,
      });
    });

    return result;
  }, [pathname, titleBread]);

  useEffect(() => {
    if (useCurrentRoute) {
      setBreadcrumbItems(generateBreadcrumb);
    } else if (items?.length) {
      setBreadcrumbItems(items);
    } else {
      setBreadcrumbItems([{ name: 'Início', path: '/' }]);
    }
  }, [useCurrentRoute, items, generateBreadcrumb, titleBread]);

  return (
    <nav className={`${styles.breadcrumb} ${className}`}>
      <ol className={styles.breadcrumbList}>
        {breadcrumbItems.map((item, i) => {
          const isLast = i === breadcrumbItems.length - 1;

          return (
            <li key={`${item.name}-${i}`} className={styles.breadcrumbItem}>
              {isLast ? (
                <span className={styles.current}>{item.name}</span>
              ) : (
                <>
                  {item.path ? (
                    <SafeLink to={item.path} className={styles.breadcrumbLink}>
                      {item.name}
                    </SafeLink>
                  ) : (
                    <span>{item.name}</span>
                  )}
                  <span className={styles.separator}>/</span>
                </>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
